import React, { useEffect, useState } from 'react';
import { filter, tap } from 'rxjs/operators';
import { merge, Subject, takeUntil } from 'rxjs';
import { useParams } from 'react-router-dom';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Button, Modal } from '@sololearnorg/sol';
import { Container, i18n } from '../../../../symphony';

import { useCourseComplete } from './useCourseComplete';
import { DownloadCertificate } from '../Modals/DownloadCertificate/DownloadCertificate';
import { SlLEContext } from '../../global-constants';
import { SlDataService } from '../../services/sl-data.service';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { ActionType, RequestType } from '../../global-interface';
import { ICertificate } from '../../../../api/public/certificates-api';

import s from './Certificate.scss';

export function Certificate() {
  useStyles(s);
  const { courseAlias, pathAlias } = useParams();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [certificate, setCertificate] = useState<ICertificate>(null);
  const { cdnApiHostV2 } = Container.take('global', 'envUrl') as { cdnApiHostV2: string; };

  const { actions$ } = Container.take(SlLEContext, SlActions);

  const {
    isCourseCompleted,
    setIsCourseCompleted,
    data: { path, title, description },
  } = useCourseComplete(() => {
    actions$.next({
      type: ActionType.OnCertificateCourseComplete,
      requestType: RequestType.Parallel,
      data: {
        alias: courseAlias || pathAlias,
      },
    });
  });

  useEffect(() => {
    const { course$, learningPath$, certificate$ } = Container.take(
      SlLEContext,
      SlDataService,
    );
    const unsubscriber: Subject<void> = new Subject<void>();

    merge(
      course$.pipe(
        filter((course) => !!course
          && course.materialInfo.header.alias === courseAlias
          && course.materialInfo.status.isCompleted),
        tap(() => setIsCourseCompleted(true)),
      ),
      learningPath$.pipe(
        filter((learningPath) => learningPath?.units.length
          && learningPath.alias === pathAlias
          && !learningPath.units.some((unit) => !unit.courseSubtree.materialInfo.status.isCompleted)),
        tap(() => setIsCourseCompleted(true)),
      ),
      certificate$.pipe(
        filter((c) => !!c),
        tap(setCertificate),
      ),
    )
      .pipe(takeUntil(unsubscriber))
      .subscribe();

    return () => {
      unsubscriber.next();
      unsubscriber.complete();
    };
  }, []);

  const certificateCTAClick = () => {
    actions$.next({
      type: ActionType.OnCertificateCTAClick,
      requestType: RequestType.Parallel,
    });

    setShowModal(true);
  };

  return (
    <div className="le-certificate" sl-test-data="cmpCertificate">
      <img
        src={`${cdnApiHostV2}/${path}`}
        alt="certificate"
        className="le-certificate__image"
      />
      <h3 className="le-certificate__title">{i18n.t(title)}</h3>
      <p className="le-certificate__description">{i18n.t(description)}</p>
      <Button
        className="le-certificate__button"
        size="m"
        onClick={certificateCTAClick}
        sl-test-data="btnCertificate"
        disabled={!isCourseCompleted}
      >
        {i18n.t('web-le.claim-certificate-button')}
      </Button>
      {showModal && (
        <Modal.Custom
          onClose={() => setShowModal(false)}
          dataTestid="cmpDownloadCertificate"
        >
          <DownloadCertificate certificate={certificate} />
        </Modal.Custom>
      )}
    </div>
  );
}
